import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Menu,
  X,
  User,
  LogOut,
  ChevronDown,
  Gift,
  Trophy,
  Wrench,
} from "lucide-react";
import { useAuthStore } from "../../stores/authStore";
import Button from "../ui/Button";
import ThemeToggle from "../ui/ThemeToggle";

import { useThemeStore } from "../../stores/themeStore";

const NAV_LINKS = [
  { label: "Services", to: "/services" },
  { label: "My Bookings", to: "/bookings", auth: true },
  { label: "Subscription", to: "/subscriptions" },
];

export default function Navbar({ onLoginClick }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const { isAuthenticated, user, logout } = useAuthStore();
  const navigate = useNavigate();
  const { theme } = useThemeStore();

  const handleLogout = () => {
    logout();
    setProfileOpen(false);
    navigate("/");
  };

  return (
    <nav className="bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800 sticky top-0 z-50">
      <div className="container-app">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="shrink-0">
            {/* <img
              src="/dm_logo-updated.png"
              alt="MechOnGo Automobiles"
              className="h-20 w-auto"
            /> */}
            <img
              src={
                theme === "dark" ||
                (theme === "system" &&
                  window.matchMedia("(prefers-color-scheme: dark)").matches)
                  ? "/dm_logo-2-updated.png"
                  : "/dm_logo-updated.png"
              }
              alt="MechOnGo Automobiles"
              className={
                theme === "dark" ||
                (theme === "system" &&
                  window.matchMedia("(prefers-color-scheme: dark)").matches)
                  ? "h-14 w-auto"
                  : "h-14 w-auto"
              }
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-6">
            {NAV_LINKS.map((link) => {
              if (link.auth && !isAuthenticated) return null;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className="text-sm font-medium text-neutral-600 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  {link.label}
                </Link>
              );
            })}
          </div>

          {/* Desktop right */}
          <div className="hidden md:flex items-center gap-2">
            <ThemeToggle />
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:text-primary-600 dark:hover:text-primary-400 transition-colors"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <User
                      size={16}
                      className="text-primary-600 dark:text-primary-400"
                    />
                  </div>
                  <span>{user?.name || "Profile"}</span>
                  <ChevronDown size={14} />
                </button>

                {profileOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setProfileOpen(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white dark:bg-neutral-800 rounded-lg shadow-dropdown border border-neutral-200 dark:border-neutral-700 py-1 z-50">
                      <Link
                        to="/profile"
                        onClick={() => setProfileOpen(false)}
                        className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        My Profile
                      </Link>
                      <Link
                        to="/wallet"
                        onClick={() => setProfileOpen(false)}
                        className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        Wallet
                      </Link>
                      <Link
                        to="/cars"
                        onClick={() => setProfileOpen(false)}
                        className="block px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        My Cars
                      </Link>
                      <Link
                        to="/rr"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        <Wrench size={14} className="text-neutral-400" />
                        My Repairs
                      </Link>
                      <Link
                        to="/referral"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        <Gift size={14} className="text-primary-500" />
                        Refer & Earn
                      </Link>
                      <Link
                        to="/milestones"
                        onClick={() => setProfileOpen(false)}
                        className="flex items-center gap-2 px-4 py-2 text-sm text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-700"
                      >
                        <Trophy size={14} className="text-amber-500" />
                        Milestones
                      </Link>
                      <hr className="my-1 border-neutral-200 dark:border-neutral-700" />
                      <button
                        onClick={handleLogout}
                        className="w-full text-left px-4 py-2 text-sm text-error-600 hover:bg-error-50 dark:hover:bg-neutral-700 flex items-center gap-2"
                      >
                        <LogOut size={14} />
                        Logout
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : (
              <Button size="sm" onClick={onLoginClick}>
                Login
              </Button>
            )}
          </div>

          {/* Mobile right — theme toggle + hamburger */}
          <div className="md:hidden flex items-center gap-1">
            <ThemeToggle />
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="p-2 text-neutral-600 dark:text-neutral-300"
            >
              {mobileOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden border-t border-neutral-200 dark:border-neutral-800 py-3">
            {NAV_LINKS.map((link) => {
              if (link.auth && !isAuthenticated) return null;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileOpen(false)}
                  className="block px-2 py-2.5 text-sm font-medium text-neutral-700 dark:text-neutral-200 hover:text-primary-600 dark:hover:text-primary-400"
                >
                  {link.label}
                </Link>
              );
            })}
            <hr className="my-2 border-neutral-200 dark:border-neutral-700" />
            {isAuthenticated ? (
              <>
                <Link
                  to="/profile"
                  onClick={() => setMobileOpen(false)}
                  className="block px-2 py-2.5 text-sm text-neutral-700 dark:text-neutral-200"
                >
                  My Profile
                </Link>
                <Link
                  to="/rr"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-2 py-2.5 text-sm text-neutral-700 dark:text-neutral-200"
                >
                  <Wrench size={14} className="text-neutral-400" />
                  My Repairs
                </Link>
                <Link
                  to="/referral"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-2 py-2.5 text-sm text-neutral-700 dark:text-neutral-200"
                >
                  <Gift size={14} className="text-primary-500" />
                  Refer & Earn
                </Link>
                <Link
                  to="/milestones"
                  onClick={() => setMobileOpen(false)}
                  className="flex items-center gap-2 px-2 py-2.5 text-sm text-neutral-700 dark:text-neutral-200"
                >
                  <Trophy size={14} className="text-amber-500" />
                  Milestones
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileOpen(false);
                  }}
                  className="block w-full text-left px-2 py-2.5 text-sm text-error-600"
                >
                  Logout
                </button>
              </>
            ) : (
              <div className="px-2 pt-1">
                <Button
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    onLoginClick?.();
                    setMobileOpen(false);
                  }}
                >
                  Login
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
